/**
 * OpportunityCard Component
 * Displays a single opportunity in list view with key information
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Bell, BellOff, Bookmark, Building2, Calendar, ChevronDown, ChevronUp, Clock, Sparkles } from 'lucide-react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useOpportunityAlert } from '../hooks/useOpportunityAlert';
import { useSavedOpportunity } from '../hooks/useSavedOpportunity';
import { CardCoverage } from './CardCoverage';
const SOURCE_DISPLAY = {
    'sam-api-v2': {
        label: 'SAM.gov',
        dotClass: 'bg-blue-500'
    },
    ebuy: {
        label: 'eBuy',
        dotClass: 'bg-emerald-500'
    },
    t4ng: {
        label: 'T4NG',
        dotClass: 'bg-violet-500'
    }
};
export function getSourceDisplay(scrapeSource) {
    if (!scrapeSource) return SOURCE_DISPLAY['sam-api-v2'];
    return SOURCE_DISPLAY[scrapeSource] || {
        label: scrapeSource,
        dotClass: 'bg-muted-foreground'
    };
}
// Block-level elements whose boundaries should become whitespace, so adjacent
// blocks/cells/list-items read as "A B" rather than smushing into "AB".
const BLOCK_SELECTOR = 'p,div,li,ul,ol,tr,td,th,h1,h2,h3,h4,h5,h6,section,article,blockquote,header,footer';
// Only route a string through the HTML parser when it actually looks like HTML —
// a plausible tag (`<tag …>`) or a TERMINATED entity (`&amp;`, `&#39;`). This keeps
// plain text with a stray `<` ("load <max then") or a bare `&` ("R&D", "Tom&reg")
// from being eaten/mangled by the parser, and skips the parse cost for plain text.
const LOOKS_LIKE_TAG = /<\/?[a-zA-Z][^>]*>/;
const LOOKS_LIKE_ENTITY = /&(?:[a-zA-Z][a-zA-Z0-9]*|#\d+|#x[0-9a-fA-F]+);/;
/**
 * Converts an HTML description/summary to plain text for the compact card brief.
 * SAM.gov (and other sources) wrap descriptions in markup — <p>, <strong>,
 * &amp;, &nbsp;, … — which React would render as literal, visible tags/entities
 * in the brief. The rich markup is preserved in the detail modal (rendered there
 * via DOMPurify); on the card we only want the readable text: tags stripped,
 * entities decoded, script/style content dropped, and block boundaries spaced.
 */ export function htmlToPlainText(value) {
    if (!value) return '';
    if (!LOOKS_LIKE_TAG.test(value) && !LOOKS_LIKE_ENTITY.test(value)) return value;
    // Non-DOM environments (e.g. SSR) have no DOMParser — degrade to a crude strip
    // rather than throwing and taking down the whole card.
    if (typeof DOMParser === 'undefined') {
        return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    }
    const doc = new DOMParser().parseFromString(value, 'text/html');
    // Drop script/style so their raw text (JS/CSS) never surfaces in the brief.
    for (const el of doc.body.querySelectorAll('script,style,noscript'))el.remove();
    for (const el of doc.body.querySelectorAll(BLOCK_SELECTOR))el.append(' ');
    for (const el of doc.body.querySelectorAll('br'))el.replaceWith(' ');
    return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}
function formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}
function getDaysUntilDeadline(deadline) {
    if (!deadline) return null;
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}
export const OpportunityCard = /*#__PURE__*/ memo(function OpportunityCard({ opportunity, onClick, inspected = false }) {
    const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
    const [showExpandButton, setShowExpandButton] = useState(false);
    const summaryRef = useRef(null);
    // Saved/alert status is seeded from the list response: ListOpportunities resolves
    // each row's per-user isSaved / isAlerted server-side (batch-looked-up), so the
    // hooks hold those values and skip the per-card GET. This is what removed the old
    // N+1 — 20 cards × 2 status GETs = 40 requests for data the list already returned.
    const { isEnabled: isAlertEnabled, toggleAlert, isLoading: isAlertLoading, error: alertError } = useOpportunityAlert(opportunity.noticeId, {
        enabled: opportunity.isAlerted ?? false
    });
    const { isSaved, toggleSaved, isLoading: isSaveLoading, error: saveError } = useSavedOpportunity(opportunity.noticeId, {
        saved: opportunity.isSaved ?? false,
        notes: opportunity.savedNotes
    });
    const daysUntil = getDaysUntilDeadline(opportunity.responseDeadLine);
    const isUrgent = daysUntil !== null && daysUntil <= 7 && daysUntil > 0;
    const isSoon = daysUntil !== null && daysUntil > 7 && daysUntil <= 30;
    const isExpired = daysUntil !== null && daysUntil < 0;
    const source = getSourceDisplay(opportunity.scrapeSource);
    // Descriptions/summaries can arrive as HTML; render the plain text on the card
    // (the detail modal keeps the rich markup). Memoized so the parent's per-keystroke
    // re-renders don't re-parse the HTML on every card.
    const summaryText = useMemo(()=>htmlToPlainText(opportunity.summary), [
        opportunity.summary
    ]);
    const descriptionText = useMemo(()=>htmlToPlainText(opportunity.description), [
        opportunity.description
    ]);
    // Show error toast when alert toggle fails
    useEffect(()=>{
        if (alertError) {
            toast.error(alertError.message || 'Failed to update alert settings');
        }
    }, [
        alertError
    ]);
    // Show error toast when save toggle fails
    useEffect(()=>{
        if (saveError) {
            toast.error(saveError.message || 'Failed to save opportunity');
        }
    }, [
        saveError
    ]);
    // Check if summary text is truncated (only show expand button if needed)
    // Use ResizeObserver to detect truncation after layout is complete
    useEffect(()=>{
        const element = summaryRef.current;
        if (!element || !summaryText) {
            setShowExpandButton(false);
            return;
        }
        const checkTruncation = ()=>{
            const isTruncated = element.scrollHeight > element.clientHeight;
            setShowExpandButton(isTruncated);
        };
        // Check immediately
        checkTruncation();
        // Also check on resize to handle window resizing
        const resizeObserver = new ResizeObserver(checkTruncation);
        resizeObserver.observe(element);
        return ()=>{
            resizeObserver.disconnect();
        };
    }, [
        summaryText
    ]);
    const borderAccent = isUrgent ? 'border-l-4 border-l-destructive' : isSoon ? 'border-l-4 border-l-warning' : '';
    return /*#__PURE__*/ _jsxs("div", {
        onClick: onClick,
        "data-testid": Testids.opportunities.card,
        className: `bg-card border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${borderAccent}`,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-start justify-between mb-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex-1 mr-4",
                        children: [
                            /*#__PURE__*/ _jsx("h3", {
                                className: "font-semibold text-lg mb-1 line-clamp-2",
                                children: opportunity.title
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2 text-sm text-muted-foreground",
                                children: [
                                    /*#__PURE__*/ _jsx(Building2, {
                                        className: "h-3.5 w-3.5"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "line-clamp-1",
                                        children: opportunity.organization.name
                                    })
                                ]
                            }),
                            opportunity.fullAgencyHierarchy && /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground mt-1 line-clamp-1",
                                children: opportunity.fullAgencyHierarchy
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2 shrink-0",
                        children: [
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: (e)=>{
                                    e.stopPropagation();
                                    toggleAlert(!isAlertEnabled);
                                },
                                disabled: isAlertLoading,
                                className: `p-1.5 rounded-md transition-colors ${isAlertEnabled ? 'text-warning hover:bg-warning/10' : 'text-muted-foreground hover:text-foreground hover:bg-accent'} disabled:opacity-50 disabled:cursor-not-allowed`,
                                "aria-label": isAlertEnabled ? 'Disable alerts for this opportunity' : 'Enable alerts for this opportunity',
                                title: isAlertEnabled ? 'Alerts enabled - Click to disable' : 'Enable alerts for updates',
                                children: isAlertEnabled ? /*#__PURE__*/ _jsx(Bell, {
                                    className: "h-4 w-4 fill-current"
                                }) : /*#__PURE__*/ _jsx(BellOff, {
                                    className: "h-4 w-4"
                                })
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: (e)=>{
                                    e.stopPropagation();
                                    toggleSaved(!isSaved);
                                },
                                disabled: isSaveLoading,
                                className: `p-1.5 rounded-md transition-colors ${isSaved ? 'text-primary hover:bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-accent'} disabled:opacity-50 disabled:cursor-not-allowed`,
                                "aria-label": isSaved ? 'Remove from saved' : 'Save opportunity',
                                title: isSaved ? 'Saved - Click to remove' : 'Save for later',
                                children: /*#__PURE__*/ _jsx(Bookmark, {
                                    className: `h-4 w-4 ${isSaved ? 'fill-current' : ''}`
                                })
                            }),
                            opportunity.responseDeadLine && !isExpired && /*#__PURE__*/ _jsxs(Badge, {
                                variant: isUrgent ? 'destructive' : 'secondary',
                                children: [
                                    /*#__PURE__*/ _jsx(Clock, {
                                        className: "h-3 w-3 mr-1"
                                    }),
                                    daysUntil === 0 ? 'Due Today' : `${daysUntil}d left`
                                ]
                            }),
                            isExpired && /*#__PURE__*/ _jsx(Badge, {
                                variant: "outline",
                                children: "Expired"
                            })
                        ]
                    })
                ]
            }),
            summaryText ? /*#__PURE__*/ _jsx("div", {
                className: "mb-2",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-start gap-2 bg-muted/30 rounded-md p-2",
                    children: [
                        /*#__PURE__*/ _jsx(Sparkles, {
                            className: "h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            ref: summaryRef,
                            "data-testid": Testids.opportunities.cardBrief,
                            className: `text-sm text-foreground flex-1 ${isSummaryExpanded ? '' : 'line-clamp-2'}`,
                            children: summaryText
                        }),
                        showExpandButton && /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            onClick: (e)=>{
                                e.stopPropagation();
                                setIsSummaryExpanded(!isSummaryExpanded);
                            },
                            className: "text-muted-foreground hover:text-foreground transition-colors shrink-0 ml-1",
                            "aria-label": isSummaryExpanded ? 'Collapse summary' : 'Expand summary',
                            children: isSummaryExpanded ? /*#__PURE__*/ _jsx(ChevronUp, {
                                className: "h-3.5 w-3.5"
                            }) : /*#__PURE__*/ _jsx(ChevronDown, {
                                className: "h-3.5 w-3.5"
                            })
                        })
                    ]
                })
            }) : /*#__PURE__*/ _jsx("p", {
                "data-testid": Testids.opportunities.cardBrief,
                className: "text-sm text-muted-foreground mb-2 line-clamp-2",
                children: descriptionText
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between text-xs text-muted-foreground",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-4",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Calendar, {
                                        className: "h-3 w-3"
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        children: [
                                            "Posted ",
                                            formatDate(opportunity.postedDate)
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Badge, {
                                variant: "outline",
                                className: "text-xs gap-1.5",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: `h-1.5 w-1.5 rounded-full ${source.dotClass}`
                                    }),
                                    source.label
                                ]
                            }),
                            inspected && /*#__PURE__*/ _jsx(CardCoverage, {
                                noticeId: opportunity.noticeId
                            }),
                            opportunity.typeOfSetAsideDescription && /*#__PURE__*/ _jsx(Badge, {
                                variant: "outline",
                                className: "text-xs",
                                children: opportunity.typeOfSetAsideDescription
                            })
                        ]
                    }),
                    opportunity.naicsCodes.length > 0 && /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-1",
                        children: [
                            opportunity.naicsCodes.slice(0, 2).map((code)=>/*#__PURE__*/ _jsx(Badge, {
                                    variant: "secondary",
                                    className: "text-xs font-mono",
                                    children: code
                                }, code)),
                            opportunity.naicsCodes.length > 2 && /*#__PURE__*/ _jsxs(Badge, {
                                variant: "secondary",
                                className: "text-xs",
                                children: [
                                    "+",
                                    opportunity.naicsCodes.length - 2
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
});
